import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class A11yFocusService {
  private readonly document = inject(DOCUMENT);
  private readonly focusTrapCleanups = new Map<HTMLElement, () => void>();
  private _previousFocus: HTMLElement | null = null;

  public capturePreviousFocus(): void {
    this._previousFocus = this.document.activeElement instanceof HTMLElement
      ? this.document.activeElement
      : null;
  }

  public restoreFocus(): void {
    const previousFocus = this._previousFocus;
    this._previousFocus = null;

    if (!previousFocus || !this.document.body.contains(previousFocus)) {
      return;
    }

    previousFocus.focus();
  }

  public focusInitialElement(container: HTMLElement): void {
    const firstFocusableElement = this.getFocusableElements(container)[0];

    (firstFocusableElement ?? container).focus();
  }

  public trapFocus(container: HTMLElement): void {
    if (this.focusTrapCleanups.has(container)) {
      return;
    }

    const keydownHandler = (event: KeyboardEvent): void => {
      if (event.key !== 'Tab') {
        return;
      }

      const focusableElements = this.getFocusableElements(container);

      if (!focusableElements.length) {
        event.preventDefault();
        container.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && this.document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
      else if (!event.shiftKey && this.document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    container.addEventListener('keydown', keydownHandler);
    this.focusTrapCleanups.set(container, () => container.removeEventListener('keydown', keydownHandler));
  }

  public cleanup(container?: HTMLElement): void {
    if (container) {
      this.focusTrapCleanups.get(container)?.();
      this.focusTrapCleanups.delete(container);
      return;
    }

    this.focusTrapCleanups.forEach((cleanup) => cleanup());
    this.focusTrapCleanups.clear();
  }

  private getFocusableElements(container: HTMLElement): HTMLElement[] {
    return Array.from(container.querySelectorAll<HTMLElement>([
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(','))).filter((element) => this.isVisible(element));
  }

  private isVisible(element: HTMLElement): boolean {
    return !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
  }
}
